import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box, Toolbar, Typography, Link, Modal, Button } from "@mui/material";
import axios from "axios";
import "./App.css";
import Navbar from "./components/navbar/Navbar";
import Dataset from "./components/dataset/Dataset";
import Datapoint from "./components/datapoint/datapointV2";
import Login from "./components/login/Login";
// import ErrorBoundary from "./components/errorBoundary/ErrorBoundary";
import { ErrorBoundary } from "react-error-boundary";
import MagicUrl from "./components/magicURL/MagicUrl"
import HomePage from "./components/home/Home";
import PreHome from "./components/preHome/PreHome";
import {
  tryPersistWithoutPromptingUser,
  promptUserForStoragePermission,
} from "./indexDB/persistent";
import UnsyncDatapoints from "./components/dataPointsList/UnsyncDatapoints";
import ViewDatapoint from "./components/datapoint/ViewDatapoint";
import ResumeDatapoint from "./components/datapoint/ResumeDatapoint";
import SyncDatapoint from "./components/dataPointsList/SyncDatapoint";
import Drafts from "./components/dataPointsList/Drafts"
import { db } from "./indexDB/model";
import Fallback from "./components/errorBoundary/ErrorBoundary";
import {getAllDataPoints, handleSyncAll} from "./indexDB/utils/datapoint"
import { getAllDrafts } from "./indexDB/utils/draft";
import { validateJwtToken } from "./utils/methods";
import useNetworkStatus from "./hooks/useNetworStatus";
import { getAccessTokenUsingRefreshToken } from "./api/login";
import ConfirmDialogBox from "./components/shared/ConfirmDialogBox";
import { captureAppError } from "./sentry";

const defaultConfirmDialogOjb = { open: false, text: undefined, type: undefined, buttonText: undefined}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSyncDialog, setOpenSyncDialog] = useState(false);
  const [homePageCounts, setHomePageCounts] = useState({draftCount: 0, syncCount: 0});
  const [confirmDialog, setConfirmDialog] = useState(defaultConfirmDialogOjb)
  const isOnline = useNetworkStatus();
  const [bgsyncing, setBgSyncing] = useState(false);
  
  const getAllCounts = async () => {
    try {
      const datapoint_length = await getAllDataPoints()
      const draft_length = await getAllDrafts()
      setHomePageCounts({'draftCount' : draft_length.length, 'syncCount': datapoint_length.length});
    } catch (error) {
      captureAppError(error, {
        tags: { source: "App.getAllCounts" },
      });
    }
  }

  useEffect(() => {
    async function checkUser() {
      try {
        const user = await db.users.where("id").equals(1).first();
        const {valid: validRefreshToken} = validateJwtToken(user?.refreshToken);
        // once refresh token implemented remove user.email from the condition and make it if (user && validRefreshToken)
        if (user && user.username && (validRefreshToken || user.email)) {
          localStorage.setItem("username", user.username)
          setLoggedIn(true);
          getAllCounts();
          const {valid: validAccessToken, expireIn: accessTokenExpiresIn} = validateJwtToken(localStorage.getItem('token'));
          if ((!validAccessToken || accessTokenExpiresIn < 3) && isOnline) {
            // Once refresh token implemented use user.refreshToken instead of user.password
            getAccessTokenUsingRefreshToken(user.email, user.password)
          }
        }
      } catch (error) {
        captureAppError(error, {
          tags: { source: "App.checkUser" },
        });
      }
    }
    checkUser();
  }, []);

  // This ensures if user is online, user has valid access token
  useEffect(() => {
    let interval;
    if (isOnline) {
      (async () => {
        try {
          const {valid: validAccessToken, expireIn: accessTokenExpiresIn} = validateJwtToken(localStorage.getItem('token'));
          if (!validAccessToken || accessTokenExpiresIn < 2) {
            const user = await db.users.where("id").equals(1).first();
            const {valid: validRefreshToken} = validateJwtToken(user?.refreshToken);
            if (validRefreshToken || user?.email) {
              // Once Refresh token implemented revove email and password, just use refresh token
              getAccessTokenUsingRefreshToken(user.email, user.password)
            }
          }
          if (validAccessToken) {
            setTimeout(() => {
              handleSyncAll(handleLogout).then(() =>{
                getAllCounts();
              }).catch((error) => {
                captureAppError(error, {
                  tags: { source: "App.initialSyncTimeout" },
                });
              });
            }, 1 * 60 * 1000)
          }
        } catch (error) {
          captureAppError(error, {
            tags: { source: "App.onlineEffect" },
          });
        }
      })();
      interval = setInterval(() => {
        setBgSyncing(true)
        handleSyncAll(handleLogout).then(() =>{
          setBgSyncing(false);
          getAllCounts();
        }).catch((error) => {
          setBgSyncing(false);
          captureAppError(error, {
            tags: { source: "App.syncInterval" },
          });
        });
      }, 30 * 60 * 1000)
    }
    return () => {
      clearInterval(interval)
    }
  }, [isOnline]);

  useEffect(() => {
    async function initStoragePersistence() {
      try {
        const persist = await tryPersistWithoutPromptingUser();
        switch (persist) {
          case "never":
            console.log("Not possible to persist storage");
            break;
          case "persisted":
            console.log("Successfully persisted storage silently");
            break;
          case "prompt":
            console.log("Not persisted, but we may prompt user when we want to");
            await promptUserForStoragePermission();
            break;
        }
      } catch (error) {
        captureAppError(error, {
          tags: { source: "App.initStoragePersistence" },
        });
      }
    }
    initStoragePersistence();
    const handleBeforeUnload = (event) => {
      // Perform actions or show a warning message before the page reloads
      const confirmationMessage = "Are you sure you want to leave?";
      event.returnValue = confirmationMessage; // Standard for most browsers
      return confirmationMessage; // For some older browsers
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Cleanup: Remove the event listener when the component unmounts
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleLogout = (errorMessage) => {
    setLoggedIn(false);
    sessionStorage.clear()
    localStorage.clear();
    setErrorMessage(errorMessage);
    setErrorModalOpen(true);
  };

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
  };

  const handleConfirmClick = () => {
    switch (confirmDialog.type) {
      case "login":
        console.log("confirm login with different creds")
        break
      case "logout":
        sessionStorage.clear();
        localStorage.clear()
        db.delete()
        setConfirmDialog(defaultConfirmDialogOjb)
        setLoggedIn(false);
        window.location.href = '/login';
        break
      case "info":
        setConfirmDialog(defaultConfirmDialogOjb)
      default:
        console.log("default case")
    }
  }

  return (
    <>
      <Router>
        <ErrorBoundary
          FallbackComponent={Fallback}
          onReset={() => setLoggedIn(false)}
          onError={(error, info) => {
            captureAppError(error, {
              tags: { source: "react-error-boundary" },
              extra: { componentStack: info?.componentStack },
            });
          }}
        >
          <div
            className="App"
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
            }}
          >
            {
            window.location.pathname !== '/magic' && (
              <Navbar
                setConfirmDialog={setConfirmDialog}
                loggedIn={loggedIn}
              />
            )}
            <ConfirmDialogBox confirmDialog={confirmDialog} setConfirmDiaog={setConfirmDialog} handleConfirm={handleConfirmClick} />
            <Toolbar />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <Routes>
                {loggedIn ? (
                  <>
                    <Route
                      path="/"
                      element={
                      <PreHome
                        openSyncDialog={openSyncDialog}
                        setOpenSyncDialog={setOpenSyncDialog}
                        setConfirmDialog={setConfirmDialog}
                        homePageCounts = {homePageCounts}
                        handleLogout={handleLogout}
                        getAllCounts={getAllCounts}
                      />
                      }
                    />
                    <Route
                      path="/home"
                      element={
                        <HomePage
                          handleLogout={handleLogout}
                          setDataset={setDataset}
                        />
                      }
                    />
                    <Route
                      path="/new-entry"
                      element={
                        <Datapoint
                          dataset={dataset}
                          handleLogout={handleLogout}
                          getAllCounts={getAllCounts}
                        />
                      }
                    />
                    <Route
                      path="/unsync-entry"
                      element={<UnsyncDatapoints setConfirmDialog={setConfirmDialog} bgsyncing={bgsyncing} handleLogout={handleLogout} homePageCounts = {homePageCounts} />}
                    />
                    <Route
                      path="/sync-entry"
                      element={<SyncDatapoint handleLogout={handleLogout} />}
                    />
                    <Route
                      path="/drafts"
                      element={<Drafts handleLogout={handleLogout} />}
                    />
                    <Route path="/view-form" element={<ViewDatapoint />} />
                    <Route path="/resume-draft" element={<ResumeDatapoint handleLogout={handleLogout}/>} />
                  </>
                ) : (
                  <Route
                    path="/login"
                    element={
                      <Login
                        setOpenSyncDialog={setOpenSyncDialog}
                        handleLogout={handleLogout}
                        setLoggedIn={setLoggedIn}
                      />
                    }
                  />
                )}
                <Route path="/magic" element={<MagicUrl handleLogout={handleLogout}/>} />

                <Route
                  path="/*"
                  element={
                    loggedIn ? <Navigate to="/" /> : <Navigate to="/login" />
                  }
                />
              </Routes>
            </Box>
          </div>
        </ErrorBoundary>
      </Router>
      <Modal
        open={errorModalOpen}
        onClose={handleCloseErrorModal}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="error-modal-title" variant="h6" component="div">
            Error
          </Typography>
          <Typography id="error-modal-description" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
          <Button
            onClick={handleCloseErrorModal}
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </>
  );
}

export default App;
